import Input from '@/components/Input';
import Select from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import type { SharedCustomField } from '@/types/serializers';
import MultiSelect from './MultiSelect';
import DatePickerModal from './DatePickerModal';

type CustomFieldValue = string | number | string[];

// 入力文字列から数字以外を除去する。type="number" は IME（日本語変換）を防げず
// スピナー（右端の上下矢印）も出るため、type="text" + inputMode にして数字だけ残す。
const sanitizeNumeric = (value: string) => value.replace(/\D/g, '');

interface CustomFieldInputProps {
  field: SharedCustomField;
  value?: CustomFieldValue;
  onChange: (value: CustomFieldValue) => void;
  onBlur?: () => void;
  /** 基本情報と同じくエラー時に入力枠を赤くする */
  error?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * SharedCustomField の種別に応じて入力 UI を出し分ける汎用コンポーネント
 */
export default function CustomFieldInput({
  field,
  value,
  onChange,
  onBlur,
  error,
  placeholder,
  disabled,
  className,
}: CustomFieldInputProps) {
  const stringValue = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  const stringArrayValue = Array.isArray(value) ? value.map(String) : [];

  // 複数選択
  if (field.field_type === 'multiple_select') {
    const options = field.custom_field_options.map((option) => ({
      id: option.id,
      name: option.value,
    }));
    const selectedIds = stringArrayValue.map((v) => Number(v)).filter((v) => Number.isFinite(v));

    return (
      <MultiSelect
        options={options}
        value={selectedIds}
        onChange={(next) => {
          onChange(next.map(String));
          onBlur?.();
        }}
        placeholder={placeholder || '選択してください'}
        disabled={disabled}
        className={className}
      />
    );
  }

  // 単一選択
  if (field.field_type === 'single_select') {
    return (
      <Select
        items={field.custom_field_options.map((option) => ({
          value: option.value,
          label: option.value,
        }))}
        value={stringValue}
        onValueChange={(next) => {
          onChange(next);
          onBlur?.();
        }}
        defaultLabel={placeholder || '選択してください'}
        disabled={disabled}
        className={className}
      />
    );
  }

  // 数値
  if (field.field_type === 'number') {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Input
            type="text"
            inputMode="numeric"
            value={stringValue}
            onChange={(e) => {
              const next = sanitizeNumeric(e.target.value);
              onChange(next === '' ? '' : Number(next));
            }}
            onBlur={onBlur}
            error={error}
            placeholder={placeholder}
            disabled={disabled}
            className={className}
          />
        </div>
        {field.unit_name && (
          <span className="text-body-sm whitespace-nowrap text-sub">{field.unit_name}</span>
        )}
      </div>
    );
  }

  // 長文
  if (field.field_type === 'long_text') {
    return (
      <Textarea
        value={stringValue}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        isError={error}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        rows={5}
      />
    );
  }

  // 日付・日時
  if (field.field_type === 'date' || field.field_type === 'datetime') {
    return (
      <DatePickerModal
        type={field.field_type}
        value={stringValue}
        onChange={(value) => {
          onChange(value);
          onBlur?.();
        }}
        placeholder={placeholder}
        className={className}
      />
    );
  }

  // 文字列（デフォルト含む）
  return (
    <Input
      value={stringValue}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      error={error}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
    />
  );
}
